/**
 * The skills API client: Bearer on every call, one silent refresh on 401,
 * typed errors with the backend's {statusCode, code} body.
 */

import { runtimeConfig } from './config';
import { clearSession, freshAccessToken, getStoredRefreshToken, refreshAccessToken } from './auth';
import type {
  BranchView,
  GrantView,
  MapView,
  NodeFull,
  PlanItem,
  ProfileCard,
  ProfileOverview,
  StateView,
  TimelinePage,
  TodayView,
} from './types';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly details?: unknown,
  ) {
    super(code);
  }
}

interface Req {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  path: string;
  body?: unknown;
  query?: Record<string, string | undefined>;
}

async function once<T>(r: Req, token: string | null): Promise<T> {
  const url = new URL(runtimeConfig().skillsApiUrl + r.path);
  for (const [k, v] of Object.entries(r.query ?? {}))
    if (v !== undefined) url.searchParams.set(k, v);
  const headers: Record<string, string> = {};
  if (r.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, {
    method: r.method ?? 'GET',
    headers,
    body: r.body !== undefined ? JSON.stringify(r.body) : undefined,
  });
  if (!res.ok) {
    const parsed = (await res.json().catch(() => null)) as {
      code?: string;
      details?: unknown;
    } | null;
    throw new ApiError(res.status, parsed?.code ?? `HTTP_${res.status}`, parsed?.details);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function api<T>(r: Req): Promise<T> {
  const token = await freshAccessToken();
  try {
    return await once<T>(r, token);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && getStoredRefreshToken() !== null) {
      const fresh = await refreshAccessToken().catch(() => {
        clearSession();
        throw e;
      });
      return once<T>(r, fresh);
    }
    if (e instanceof ApiError && e.status === 401) clearSession();
    throw e;
  }
}

/* ---------- typed calls ---------- */

export const Api = {
  me: () => api<{ uid: string; apc: string }>({ path: '/v1/me' }),
  map: () => api<MapView>({ path: '/v1/map' }),
  branch: (key: string) => api<BranchView>({ path: `/v1/map/branches/${key}` }),
  node: (key: string) => api<NodeFull>({ path: `/v1/map/nodes/${key}` }),
  profiles: () => api<ProfileCard[]>({ path: '/v1/profiles' }),
  createProfile: (body: unknown) =>
    api<ProfileOverview>({ method: 'POST', path: '/v1/profiles', body }),
  overview: (id: string) => api<ProfileOverview>({ path: `/v1/profiles/${id}` }),
  state: (id: string, q?: { asOf?: string; branch?: string }) =>
    api<StateView>({ path: `/v1/profiles/${id}/state`, query: q }),
  timeline: (id: string, q?: { before?: string; limit?: string; branch?: string; node?: string }) =>
    api<TimelinePage>({ path: `/v1/profiles/${id}/timeline`, query: q }),
  today: (id: string) => api<TodayView>({ path: `/v1/profiles/${id}/today` }),
  plan: (id: string) => api<PlanItem[]>({ path: `/v1/profiles/${id}/plan` }),
  addPlan: (id: string, body: unknown) =>
    api<PlanItem>({ method: 'POST', path: `/v1/profiles/${id}/plan`, body }),
  endPlan: (id: string, itemId: string) =>
    api<PlanItem>({ method: 'POST', path: `/v1/profiles/${id}/plan/${itemId}/end` }),
  assess: (id: string, body: unknown) =>
    api<{ seq: number; status: string; confirming: boolean; summary: string }>({
      method: 'POST',
      path: `/v1/profiles/${id}/assess`,
      body,
    }),
  logPractice: (id: string, body: unknown) =>
    api<{ seq: number; summary: string; practiceCount: number }>({
      method: 'POST',
      path: `/v1/profiles/${id}/practices/log`,
      body,
    }),
  revert: (id: string, body: unknown) =>
    api<{ seq: number }>({ method: 'POST', path: `/v1/profiles/${id}/revert`, body }),
  lost: (id: string, body: unknown) =>
    api<{ seq: number }>({ method: 'POST', path: `/v1/profiles/${id}/lost`, body }),
  note: (id: string, body: unknown) =>
    api<{ seq: number }>({ method: 'POST', path: `/v1/profiles/${id}/notes`, body }),
  grants: (id: string) => api<GrantView[]>({ path: `/v1/profiles/${id}/grants` }),
  shareCode: (id: string, body: unknown) =>
    api<{ code: string; expiresAt: string }>({
      method: 'POST',
      path: `/v1/profiles/${id}/share-codes`,
      body,
    }),
  revoke: (id: string, grantId: string) =>
    api<{ id: string }>({ method: 'POST', path: `/v1/profiles/${id}/grants/${grantId}/revoke` }),
  redeem: (code: string) =>
    api<{ profileId: string; displayName: string }>({
      method: 'POST',
      path: '/v1/grants/redeem',
      body: { code },
    }),
  accessLog: (id: string) =>
    api<{ uid: string; role: string; action: string; at: string }[]>({
      path: `/v1/profiles/${id}/access-log`,
    }),
  addMember: (id: string, body: unknown) =>
    api<ProfileOverview>({ method: 'POST', path: `/v1/profiles/${id}/members`, body }),
  transfer: (id: string, body: unknown) =>
    api<ProfileOverview>({ method: 'POST', path: `/v1/profiles/${id}/ownership/transfer`, body }),
  exportProfile: (id: string) => api<unknown>({ path: `/v1/profiles/${id}/export` }),
};
